import { AssessmentStepKeys } from '@/enums'
import { FormProvider } from '@/providers'
import { flipObject, setLocalStorageItem } from '@/utils'
import { equals, replace } from 'ramda'
import { useCallback, useEffect, ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { useAssessmentValues } from '../helpers'
import { makeValidationSchema, Messages } from '../validationSchema'
import { StepContainer, StepSubTitle, StepTitleWrapper } from './styles'
import { Typography } from '@/components'

const useValidationMessages = (): Messages => {
  const { t } = useTranslation()
  const validations = t('validations', { returnObjects: true }) as Record<
    string,
    string
  >

  return {
    ...validations,
    required: validations.general || '',
    startDateNotValidLe: t('start_date_not_valid_le'),
    makeMin: (min: number) => t('validations.min', { min }),
    makeMax: (max: number) => t('validations.max', { max }),
    phoneIncorrect: validations.phoneIncorrect || '',
    validEmail: validations.validEmail || '',
    selectOption: validations.selectOption || '',
    city: validations.city || '',
    country: validations.country || '',
    postalCode: validations.postalCode || '',
    street: validations.street || '',
    streetNr: validations.streetNr || '',
    name: validations.name || '',
    positive: validations.positive || '',
  }
}

const Steps = flipObject(AssessmentStepKeys) as Record<string, string>

interface StepFormProps {
  step: string
  valuesLocalStorageKey?: string
  stepLocalStorageKey?: string
  messages: {
    subtitle?: string
    subtitles?: Record<string, string>
    titles: Record<string, string>
  }
  children: ReactNode
}

interface AssessmentValues {
  [key: string]: any
  locationDetails?: {
    name?: string
  }
}

interface AssessmentValueContextType {
  values: AssessmentValues
  dispatchValues: (newValues: Record<string, any>) => void
}

const StepForm = ({
  step,
  valuesLocalStorageKey,
  stepLocalStorageKey,
  messages,
  children,
}: StepFormProps) => {
  const stepName = Steps[step]
  const validationMessages = useValidationMessages()
  const {
    values,
    dispatchValues,
  } = useAssessmentValues() as AssessmentValueContextType

  const handleChange = (newValues: any) => {
    if (!equals(newValues, values[stepName]))
      dispatchValues({ [stepName]: newValues })
  }

  useEffect(() => {
    if (valuesLocalStorageKey) {
      setLocalStorageItem(valuesLocalStorageKey, values)
    }
  }, [values, valuesLocalStorageKey])

  useEffect(() => {
    if (stepLocalStorageKey) {
      setLocalStorageItem(stepLocalStorageKey, step)
    }
  }, [step, stepLocalStorageKey])

  const validationSchema = makeValidationSchema(validationMessages)[stepName]

  return (
    <FormProvider
      initialValues={values[stepName]}
      onChange={handleChange}
      validationSchema={validationSchema}
    >
      <StepContainer>
        {messages.titles[step] ? (
          <StepTitleWrapper>
            <Typography variant="h4">
              {replace(
                '{{locationName}}',
                values.locationDetails?.name || '',
                messages.titles[step],
              )}
            </Typography>
            {messages.subtitles?.[step] ? (
              <StepSubTitle>{messages.subtitles[step]}</StepSubTitle>
            ) : null}
          </StepTitleWrapper>
        ) : null}
        {children}
      </StepContainer>
    </FormProvider>
  )
}

export interface StepWrapperMessages {
  subtitle?: string
  subtitles?: Record<string, string>
  titles: Record<string, string>
}

interface StepWrapperProps {
  messages: StepWrapperMessages
  step: string
  stepLocalStorageKey?: string
  valuesLocalStorageKey?: string
  children: ReactNode
}

export const StepWrapper = ({
  messages,
  step,
  stepLocalStorageKey,
  valuesLocalStorageKey,
  children,
}: StepWrapperProps) => {
  const Form = useCallback(
    (props: { children: ReactNode }) => (
      <StepForm
        messages={messages}
        step={step}
        stepLocalStorageKey={stepLocalStorageKey}
        valuesLocalStorageKey={valuesLocalStorageKey}
      >
        {props.children}
      </StepForm>
    ),
    [step, stepLocalStorageKey, valuesLocalStorageKey],
  )

  return <Form>{children}</Form>
}
